// task-and-subtask-activity.api.ts
import { api } from "@/redux/api";

import {
	TaskAndSubtaskCommentInfo,
	CreateTaskAndSubtaskComment,
	TaskAndSubtaskCommentInfoSchema,
	UpdateTaskAndSubtaskComment,
	TaskAndSubtaskHistoryInfo,
	TaskAndSubtaskHistoryInfoSchema,
	TaskAndSubtaskCommentResponseSchema,
} from "../../pages/TasksAndSubTasks/schema/tasks-and-subtasks-activity";

export const taskAndSubtaskActivityApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// Comments
		createComment: builder.mutation<TaskAndSubtaskCommentInfo, CreateTaskAndSubtaskComment>({
			query: (body) => ({
				url: "activity/comment",
				method: "POST",
				body,
				responseSchema: TaskAndSubtaskCommentInfoSchema,
			}),
			invalidatesTags: ["TaskAndSubtaskActivity"],
		}),

		getCommentsAll: builder.query<
			TaskAndSubtaskCommentInfo[],
			{ taskIds: number[]; subtaskIds: number[]; companyId: number }
		>({
			query: ({ taskIds, companyId, subtaskIds }) => ({
				url: `activity/comment/all?taskIds=${taskIds.join(",")}&subtaskIds=${subtaskIds.join(
					","
				)}&companyId=${companyId}`,
				responseSchema: TaskAndSubtaskCommentResponseSchema,
			}),
			providesTags: ["TaskAndSubtaskActivity"],
		}),

		getCommentsByTask: builder.query<TaskAndSubtaskCommentInfo[], number>({
			query: (taskId) => ({
				url: `activity/comment/task/${taskId}`,
				responseSchema: TaskAndSubtaskCommentInfoSchema,
			}),
			providesTags: (_result, _error, taskId) => [{ type: "TaskAndSubtaskActivity", id: taskId }],
		}),

		getCommentsBySubtask: builder.query<TaskAndSubtaskCommentInfo[], number>({
			query: (subtaskId) => ({
				url: `activity/comment/subtask/${subtaskId}`,
				responseSchema: TaskAndSubtaskCommentInfoSchema,
			}),
			providesTags: (_result, _error, subtaskId) => [
				{ type: "TaskAndSubtaskActivity", id: subtaskId },
			],
		}),

		updateComment: builder.mutation<
			TaskAndSubtaskCommentInfo,
			{ id: number } & UpdateTaskAndSubtaskComment
		>({
			query: ({ id, ...body }) => ({
				url: `activity/comment/${id}`,
				method: "PATCH",
				body,
				responseSchema: TaskAndSubtaskCommentInfoSchema,
			}),
			invalidatesTags: ["TaskAndSubtaskActivity"],
		}),

		deleteComment: builder.mutation<void, number>({
			query: (id) => ({
				url: `activity/comment/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["TaskAndSubtaskActivity"],
		}),

		// History
		getHistoryByTask: builder.query<TaskAndSubtaskHistoryInfo[], number>({
			query: (taskId) => ({
				url: `activity/history/task/${taskId}`,
				responseSchema: TaskAndSubtaskHistoryInfoSchema,
			}),
			providesTags: (_result, _error, taskId) => [{ type: "TaskAndSubtaskActivity", id: taskId }],
		}),

		getHistoryBySubtask: builder.query<TaskAndSubtaskHistoryInfo[], number>({
			query: (subtaskId) => ({
				url: `activity/history/subtask/${subtaskId}`,
				responseSchema: TaskAndSubtaskHistoryInfoSchema,
			}),
			providesTags: (_result, _error, subtaskId) => [
				{ type: "TaskAndSubtaskActivity", id: subtaskId },
			],
		}),
	}),
});

export const {
	useCreateCommentMutation,
	useGetCommentsByTaskQuery,
	useGetCommentsBySubtaskQuery,
	useUpdateCommentMutation,
	useDeleteCommentMutation,
	useGetHistoryByTaskQuery,
	useGetHistoryBySubtaskQuery,
	useGetCommentsAllQuery,
} = taskAndSubtaskActivityApi;
